// Lava palette — FLIR / DJI Lava variant.
//
// Reference: https://www.flir.com (Lava). Cold→hot sweep through
// black → dark blue → purple → maroon → red → orange → yellow →
// pale cream. Blue cold-end is what distinguishes it from the
// pure-warm "Hot Iron" / "IronRed" ramps.
//
// 11 stops with linear interpolation. Returned as RGB float triples
// in the [0, 1] range; the caller writes them straight into a
// vertex color BufferAttribute.

const LAVA_STOPS: ReadonlyArray<readonly [number, readonly [number, number, number]]> = [
  [0.0, [0.0, 0.0, 0.0]],        // black
  [0.06, [0.02, 0.03, 0.13]],    // near-black blue
  [0.16, [0.05, 0.13, 0.45]],    // dark blue
  [0.26, [0.13, 0.18, 0.62]],    // mid blue
  [0.36, [0.32, 0.16, 0.55]],    // blue-purple
  [0.46, [0.55, 0.1, 0.42]],     // purple
  [0.56, [0.78, 0.08, 0.18]],    // maroon
  [0.66, [0.95, 0.16, 0.05]],    // red
  [0.76, [1.0, 0.46, 0.0]],      // orange-red
  [0.86, [1.0, 0.72, 0.05]],     // orange
  [0.94, [1.0, 0.92, 0.25]],     // yellow
  [1.0, [1.0, 0.98, 0.85]],      // pale cream-white
] as const;

/** Map a normalized [0, 1] value to RGB. Clamps out-of-range. */
export function lavaColor(t: number): [number, number, number] {
  if (!Number.isFinite(t)) return [0.5, 0.5, 0.5];
  const x = Math.max(0, Math.min(1, t));
  for (let i = 0; i < LAVA_STOPS.length - 1; i++) {
    const [a, ca] = LAVA_STOPS[i];
    const [b, cb] = LAVA_STOPS[i + 1];
    if (x <= b) {
      const f = (x - a) / (b - a);
      return [
        ca[0] + (cb[0] - ca[0]) * f,
        ca[1] + (cb[1] - ca[1]) * f,
        ca[2] + (cb[2] - ca[2]) * f,
      ];
    }
  }
  const last = LAVA_STOPS[LAVA_STOPS.length - 1][1];
  return [last[0], last[1], last[2]];
}

/** Render a CSS linear-gradient covering the full ramp — used for the
 *  legend strip in the panel. */
export function lavaCssGradient(): string {
  const stops = LAVA_STOPS.map(([t, [r, g, b]]) => {
    const r255 = Math.round(r * 255);
    const g255 = Math.round(g * 255);
    const b255 = Math.round(b * 255);
    return `rgb(${r255}, ${g255}, ${b255}) ${(t * 100).toFixed(1)}%`;
  }).join(", ");
  return `linear-gradient(to right, ${stops})`;
}
